import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getStrategy, listStrategies } from "../api/strategies";
import { DATA_ONLY_BROKERS, listBrokerConnections } from "../api/brokers";
import { getMyPolicy, MY_POLICY_KEY } from "../api/policy";
import {
  createDeployment,
  getDeployment,
  updateDeployment,
  type DeploymentUpdate,
} from "../api/deployments";
import Button from "../components/Button";
import SymbolSearch from "../components/SymbolSearch";
import Card from "../components/Card";
import {
  NumberInput,
  noAutocorrect,
  straightenJsonQuotes,
} from "../components/NumberInput";
import StrategyParamFields, {
  useParamsFieldsSource,
} from "../components/StrategyParamFields";
import { apiErrorMessage } from "../lib/api";

type Mode = "paper" | "live";
type Timeframe = "1m" | "3m" | "5m" | "10m" | "15m" | "30m" | "1h" | "1d";

// Default params JSON per sizing mode (matches each sizer's Params fields).
const SIZING_DEFAULTS: Record<string, string> = {
  fixed_lots: '{"lots": 1}',
  fixed_rupees: '{"rupees": 100000}',
  percent_of_capital: '{"pct": 10}',
  atr_volatility_target: '{"target_vol_pct": 1}',
  risk_per_trade: '{"risk_pct": 1}',
};

interface InstrumentRow {
  symbol: string;
  exchange: string;
  instrument_type: string;
  expiry: string;
  strike: string;
  lot_size: number;
  tick_size: string;
  broker_connection_id: string;
}

/** Must match `Instrument.key` on the backend (core/instrument.py) exactly —
 *  the API validates `broker_map` against it and 400s on any mismatch.
 *
 *  Note the emptiness tests: the backend uses `strike is not None`, so a strike
 *  of "0" is KEPT there. A truthiness check here would drop it and produce a
 *  key the server rejects with "broker_map missing entries". */
function instrumentKey(i: InstrumentRow): string {
  const parts = [i.exchange, i.symbol];
  if (i.expiry !== null && i.expiry !== undefined && i.expiry !== "") {
    parts.push(i.expiry);
  }
  if (i.strike !== null && i.strike !== undefined && i.strike !== "") {
    parts.push(`${i.strike}${i.instrument_type}`);
  }
  return parts.join(":");
}

export default function DeploymentCreatePage() {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const editing = !!editId;
  const { data: existing } = useQuery({
    queryKey: ["deployment", editId],
    queryFn: () => getDeployment(editId!),
    enabled: editing,
  });

  const { data: strategies } = useQuery({ queryKey: ["strategies"], queryFn: listStrategies });
  const { data: brokers } = useQuery({
    queryKey: ["broker-connections"],
    queryFn: listBrokerConnections,
  });
  const { data: policy } = useQuery({ queryKey: MY_POLICY_KEY, queryFn: getMyPolicy });

  // Only connections that can actually route this user's orders.
  //
  // Two kinds get filtered out, and both were previously offered here and then
  // rejected by the server with a 400 AFTER submit:
  //   - data-only brokers (Upstox backfills history but has no order adapter);
  //   - connections outside the user's current broker policy, e.g. an admin
  //     lowered their account cap so the newest connection no longer trades.
  // While the policy query is in flight we fall back to showing everything
  // rather than briefly emptying the dropdown under the user.
  const routableConnections = (brokers ?? []).filter((b) => {
    if (DATA_ONLY_BROKERS.includes(b.broker)) return false;
    if (!policy) return true;
    return policy.in_policy_connection_ids.includes(b.id);
  });

  const [name, setName] = useState("");
  const [strategyKey, setStrategyKey] = useState("");
  const [mode, setMode] = useState<Mode>("paper");
  const [capital, setCapital] = useState("100000");
  const [margin, setMargin] = useState("1");
  const [timeframe, setTimeframe] = useState<Timeframe>("1m");
  const [paramsJson, setParamsJson] = useState("{}");

  // Load the selected strategy's default params into the params box (once per
  // distinct selection, so it doesn't clobber manual edits on a cache re-read).
  const { data: strategyDetail } = useQuery({
    queryKey: ["strategy", strategyKey],
    queryFn: () => getStrategy(strategyKey),
    enabled: !!strategyKey,
  });
  const lastSeededKey = useRef<string>("");
  useEffect(() => {
    if (strategyDetail && strategyDetail.key !== lastSeededKey.current) {
      setParamsJson(JSON.stringify(strategyDetail.default_params ?? {}, null, 2));
      lastSeededKey.current = strategyDetail.key;
    }
  }, [strategyDetail]);

  // Edit mode: prefill the form from the existing deployment once. Mark its
  // strategy as already-seeded so the default-params effect above doesn't
  // clobber the saved params.
  const prefilled = useRef(false);
  useEffect(() => {
    if (!existing || prefilled.current) return;
    prefilled.current = true;
    lastSeededKey.current = existing.strategy_key;
    setName(existing.name);
    setStrategyKey(existing.strategy_key);
    setMode(existing.mode);
    setCapital(String(existing.capital));
    setMargin(String(existing.margin ?? "1"));
    setTimeframe(existing.timeframe as Timeframe);
    setParamsJson(JSON.stringify(existing.params ?? {}, null, 2));
    setSizingMode(existing.sizing_mode);
    setSizingParamsJson(JSON.stringify(existing.sizing_params ?? {}));
    setDailyLossCapPct(String(existing.risk_rails?.daily_loss_cap_pct ?? "2.0"));
    setNotifyTelegram(existing.notify_telegram);
    setInstruments(
      existing.instruments.map((i) => {
        const row: InstrumentRow = {
          symbol: i.symbol,
          exchange: i.exchange,
          instrument_type: i.instrument_type,
          expiry: i.expiry ?? "",
          strike: i.strike ?? "",
          lot_size: i.lot_size ?? 1,
          tick_size: i.tick_size ?? "0.05",
          broker_connection_id: "",
        };
        row.broker_connection_id = existing.broker_map[instrumentKey(row)] ?? "";
        return row;
      }),
    );
  }, [existing]);

  // Edit mode, second pass: once the strategy's schema defaults are known, fill
  // in any param the SAVED config predates. A deployment created before a param
  // existed has no key for it, so the JSON box simply never showed it and the
  // setting looked missing from the UI entirely (hit with `loss_recovery`,
  // 2026-08-04). Saved values always win; this only ever ADDS absent keys.
  const mergedDefaults = useRef(false);
  useEffect(() => {
    if (!existing || !strategyDetail || mergedDefaults.current) return;
    if (strategyDetail.key !== existing.strategy_key) return;
    mergedDefaults.current = true;
    const defaults = strategyDetail.default_params ?? {};
    if (!Object.keys(defaults).length) return;
    setParamsJson((cur) => {
      let saved: Record<string, unknown>;
      try {
        saved = JSON.parse(cur || "{}");
      } catch {
        return cur; // mid-edit invalid JSON — never clobber what they typed
      }
      const missing = Object.keys(defaults).filter((k) => !(k in saved));
      if (!missing.length) return cur;
      return JSON.stringify({ ...defaults, ...saved }, null, 2);
    });
  }, [existing, strategyDetail]);

  // Best-effort parse of the params box for the routing controls. On invalid
  // JSON we fall back to {} (the routing section then hides until it's valid).
  const paramsObj = useMemo<Record<string, unknown>>(() => {
    try {
      const v = JSON.parse(paramsJson || "{}");
      return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }, [paramsJson]);

  // While the box is mid-edit and does not parse, the fields keep the last
  // values that did (locked) instead of the whole card disappearing.
  const paramsFieldsSource = useParamsFieldsSource(paramsJson);

  function patchParam(patch: Record<string, unknown>) {
    setParamsJson(JSON.stringify({ ...paramsObj, ...patch }, null, 2));
  }
  const [sizingMode, setSizingMode] = useState("fixed_lots");
  const [sizingParamsJson, setSizingParamsJson] = useState('{"lots": 1}');
  const [dailyLossCapPct, setDailyLossCapPct] = useState("2.0");
  const [notifyTelegram, setNotifyTelegram] = useState(true);
  const [instruments, setInstruments] = useState<InstrumentRow[]>([
    {
      symbol: "BTCUSD",
      exchange: "DELTA",
      instrument_type: "PERP",
      expiry: "",
      strike: "",
      lot_size: 1,
      tick_size: "0.05",
      broker_connection_id: "",
    },
  ]);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: createDeployment,
    onSuccess: (d) => navigate(`/deployments/${d.id}`),
    onError: (e) => setError(apiErrorMessage(e, "Failed")),
  });

  const update = useMutation({
    mutationFn: (payload: DeploymentUpdate) => updateDeployment(editId!, payload),
    onSuccess: (d) => navigate(`/deployments/${d.id}`),
    onError: (e) => setError(apiErrorMessage(e, "Failed")),
  });

  const saving = create.isPending || update.isPending;

  function updateInstrument(idx: number, patch: Partial<InstrumentRow>) {
    setInstruments(instruments.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  }

  function addInstrument() {
    setInstruments([
      ...instruments,
      {
        symbol: "",
        exchange: "NSE",
        instrument_type: "EQ",
        expiry: "",
        strike: "",
        lot_size: 1,
        tick_size: "0.05",
        broker_connection_id: "",
      },
    ]);
  }

  function removeInstrument(idx: number) {
    setInstruments(instruments.filter((_, i) => i !== idx));
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    let parsedParams: Record<string, unknown>;
    let parsedSizingParams: Record<string, unknown>;
    try {
      parsedParams = JSON.parse(paramsJson || "{}");
      parsedSizingParams = JSON.parse(sizingParamsJson || "{}");
    } catch (e) {
      setError(`Invalid JSON in params or sizing params: ${(e as Error).message}`);
      return;
    }

    if (!strategyKey) {
      setError("Pick a strategy");
      return;
    }

    const broker_map: Record<string, string> = {};
    for (const inst of instruments) {
      if (!inst.broker_connection_id) {
        setError(`Pick a broker for ${inst.symbol}`);
        return;
      }
      broker_map[instrumentKey(inst)] = inst.broker_connection_id;
    }

    const instrumentRefs = instruments.map((i) => ({
      symbol: i.symbol,
      exchange: i.exchange,
      instrument_type: i.instrument_type,
      expiry: i.expiry || null,
      strike: i.strike || null,
      lot_size: i.lot_size,
      tick_size: i.tick_size,
    }));

    // Strategy and mode are fixed at create — the PATCH schema omits them.
    const common = {
      name,
      capital,
      margin,
      timeframe,
      params: parsedParams,
      instruments: instrumentRefs,
      broker_map,
      sizing_mode: sizingMode,
      sizing_params: parsedSizingParams,
      risk_rails: { daily_loss_cap_pct: dailyLossCapPct },
      notify_telegram: notifyTelegram,
    };

    if (editing) {
      update.mutate(common);
    } else {
      create.mutate({ ...common, strategy_key: strategyKey, mode });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-semibold">
          {editing ? "Edit deployment" : "New deployment"}
        </h1>
        <Link to={editing ? `/deployments/${editId}` : "/deployments"}>
          <Button variant="ghost">← Back</Button>
        </Link>
      </div>
      {editing && (
        <p className="text-sm text-fg-subtle -mt-2">
          Strategy and mode are fixed. Changes apply the next time you start this
          deployment.
        </p>
      )}

      <form onSubmit={onSubmit} className="space-y-6">
        <Card title="Basics">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Name">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-border rounded px-3 py-2"
                required
              />
            </Field>
            <Field label="Strategy">
              <select
                value={strategyKey}
                onChange={(e) => setStrategyKey(e.target.value)}
                className="w-full border border-border rounded px-3 py-2 disabled:opacity-60"
                required
                disabled={editing}
                title={editing ? "Strategy can't change after create" : undefined}
              >
                <option value="">— pick one —</option>
                {strategies?.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.display_name} ({s.key})
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Mode">
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value as Mode)}
                className="w-full border border-border rounded px-3 py-2 disabled:opacity-60"
                disabled={editing}
                title={editing ? "Mode can't change after create" : undefined}
              >
                <option value="paper">paper</option>
                <option value="live">live</option>
              </select>
            </Field>
            <Field label="Timeframe">
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value as Timeframe)}
                className="w-full border border-border rounded px-3 py-2"
              >
                <option value="1m">1m</option>
                <option value="3m">3m</option>
                <option value="5m">5m</option>
                <option value="10m">10m</option>
                <option value="15m">15m</option>
                <option value="30m">30m</option>
                <option value="1h">1h</option>
                <option value="1d">1d</option>
              </select>
            </Field>
            <Field label="Capital">
              <input
                value={capital}
                onChange={(e) => setCapital(e.target.value)}
                inputMode="decimal"
                className="w-full border border-border rounded px-3 py-2 font-mono"
              />
            </Field>
            <Field label="Margin (×)">
              <input
                type="number"
                min={1}
                max={100}
                step="0.5"
                title="Leverage multiplier; sizer sees capital × margin. Set 1 for no leverage."
                value={margin}
                onChange={(e) => setMargin(e.target.value)}
                className="w-full border border-border rounded px-3 py-2 font-mono"
              />
            </Field>
            <Field label="Daily loss cap (% of capital)">
              <input
                value={dailyLossCapPct}
                onChange={(e) => setDailyLossCapPct(e.target.value)}
                inputMode="decimal"
                className="w-full border border-border rounded px-3 py-2 font-mono"
              />
            </Field>
            <Field label="Telegram notifications">
              <label className="flex items-start gap-2 py-2 text-sm">
                <input
                  type="checkbox"
                  checked={notifyTelegram}
                  onChange={(e) => setNotifyTelegram(e.target.checked)}
                />
                <span>Send a Telegram message on every order (needs Settings → Notifications)</span>
              </label>
            </Field>
          </div>
          <Field label="Strategy params (JSON)">
            <textarea
              value={paramsJson}
              onChange={(e) => setParamsJson(straightenJsonQuotes(e.target))}
              {...noAutocorrect}
              rows={10}
              className="w-full border border-border rounded px-3 py-2 font-mono text-base sm:text-sm"
            />
            <p className="text-xs text-fg-subtle mt-1">
              Prefilled with the strategy's defaults when you pick one. Edit as needed.
            </p>
          </Field>
        </Card>

        <StrategyParamFields
          {...paramsFieldsSource}
          patch={patchParam}
          context="deployment"
        />

        <Card title="Sizing">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Mode">
              <select
                value={sizingMode}
                onChange={(e) => {
                  const m = e.target.value;
                  setSizingMode(m);
                  setSizingParamsJson(SIZING_DEFAULTS[m] ?? "{}");
                }}
                className="w-full border border-border rounded px-3 py-2"
              >
                <option value="fixed_lots">fixed_lots</option>
                <option value="fixed_rupees">fixed_rupees</option>
                <option value="percent_of_capital">percent_of_capital</option>
                <option value="atr_volatility_target">atr_volatility_target</option>
                <option value="risk_per_trade">risk_per_trade</option>
              </select>
            </Field>
            <Field label="Params (JSON)">
              <input
                value={sizingParamsJson}
                onChange={(e) => setSizingParamsJson(straightenJsonQuotes(e.target))}
                {...noAutocorrect}
                className="w-full border border-border rounded px-3 py-2 font-mono text-base sm:text-sm"
              />
            </Field>
          </div>
        </Card>

        <Card
          title="Instruments + per-symbol broker"
          actions={
            <Button type="button" variant="secondary" size="sm" onClick={addInstrument}>
              + Add row
            </Button>
          }
        >
          {/* An empty Broker column with no explanation is useless — the most
              likely cause is an account whose only connection is Upstox, which
              can backfill history but has no order adapter at all. */}
          {brokers && routableConnections.length === 0 && (
            <div className="mb-3 text-sm text-warn-700 dark:text-warn-400 bg-warn-50 dark:bg-warn-500/10 border border-warn-200 dark:border-warn-400/30 rounded px-3 py-2.5">
              {brokers.length === 0 ? (
                <>
                  You have no broker connections yet. Add one on the{" "}
                  <Link to="/brokers" className="underline">
                    Brokers
                  </Link>{" "}
                  page before deploying.
                </>
              ) : brokers.every((b) => DATA_ONLY_BROKERS.includes(b.broker)) ? (
                <>
                  <strong>
                    {brokers
                      .map((b) => b.broker)
                      .filter((v, i, a) => a.indexOf(v) === i)
                      .join(", ")}{" "}
                    cannot place orders.
                  </strong>{" "}
                  It is a data-only connection — useful for backfilling
                  historical bars, but there is no order adapter for it. To
                  deploy a strategy you need a Zerodha, Dhan or Delta connection.
                  Ask an admin to enable one for your account.
                </>
              ) : (
                <>
                  None of your broker connections can currently route orders.
                  They are either data-only, or outside your account's current
                  broker limits — check the{" "}
                  <Link to="/brokers" className="underline">
                    Brokers
                  </Link>{" "}
                  page, or ask an admin to raise your limit.
                </>
              )}
            </div>
          )}
          {/* Search sits ABOVE the table rather than inside a cell: the
              dropdown is 420px wide and this row has seven columns, so an
              inline picker would overlap everything to its right. Mode "any"
              because a deployment trades forward — it needs a live feed, not
              history, so a symbol with no bars is a perfectly valid choice. */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
            <span className="text-xs text-muted shrink-0">Add instrument</span>
            <SymbolSearch
              mode="any"
              value={null}
              onChange={(inst) => {
                setInstruments((cur) => [
                  ...cur,
                  {
                    symbol: inst.symbol,
                    exchange: inst.exchange,
                    instrument_type: inst.instrument_type,
                    expiry: inst.expiry ?? "",
                    strike: inst.strike ?? "",
                    lot_size: inst.lot_size ?? 1,
                    tick_size: inst.tick_size ?? "0.05",
                    broker_connection_id: "",
                  },
                ]);
              }}
            />
            <span className="text-xs text-muted">
              picks up exchange, type, expiry, strike and lot size — you only
              choose the broker
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted border-b border-border/60">
                <tr>
                  <th className="py-2 pr-2">Symbol</th>
                  <th className="py-2 pr-2">Exchange</th>
                  <th className="py-2 pr-2">Type</th>
                  <th className="py-2 pr-2">Expiry</th>
                  <th className="py-2 pr-2">Strike</th>
                  <th className="py-2 pr-2">Lot</th>
                  <th className="py-2 pr-2">Broker</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {instruments.map((i, idx) => (
                  <tr key={idx} className="border-b border-border/60 last:border-0">
                    <td className="py-1 pr-2">
                      <input
                        value={i.symbol}
                        onChange={(e) => updateInstrument(idx, { symbol: e.target.value })}
                        {...noAutocorrect}
                        autoCapitalize="characters"
                        className="w-full border border-border rounded px-2 py-2 sm:py-1 font-mono text-base sm:text-xs"
                      />
                    </td>
                    <td className="py-1 pr-2">
                      <select
                        value={i.exchange}
                        onChange={(e) => updateInstrument(idx, { exchange: e.target.value })}
                        className="w-full border border-border rounded px-2 py-2 sm:py-1 font-mono text-base sm:text-xs"
                      >
                        <option>NSE</option>
                        <option>BSE</option>
                        <option>NFO</option>
                        <option>BFO</option>
                        <option>DELTA</option>
                        <option>BINANCE</option>
                      </select>
                    </td>
                    <td className="py-1 pr-2">
                      <select
                        value={i.instrument_type}
                        onChange={(e) =>
                          updateInstrument(idx, { instrument_type: e.target.value })
                        }
                        className="w-full border border-border rounded px-2 py-2 sm:py-1 font-mono text-base sm:text-xs"
                      >
                        <option>EQ</option>
                        <option>FUT</option>
                        <option>CE</option>
                        <option>PE</option>
                        <option>INDEX</option>
                        <option>PERP</option>
                        <option>SPOT</option>
                      </select>
                    </td>
                    <td className="py-1 pr-2">
                      <input
                        value={i.expiry}
                        onChange={(e) => updateInstrument(idx, { expiry: e.target.value })}
                        placeholder="YYYY-MM-DD"
                        {...noAutocorrect}
                        className="w-full border border-border rounded px-2 py-2 sm:py-1 font-mono text-base sm:text-xs"
                      />
                    </td>
                    <td className="py-1 pr-2">
                      <input
                        value={i.strike}
                        onChange={(e) => updateInstrument(idx, { strike: e.target.value })}
                        inputMode="decimal"
                        className="w-full border border-border rounded px-2 py-2 sm:py-1 font-mono text-base sm:text-xs"
                      />
                    </td>
                    <td className="py-1 pr-2">
                      <NumberInput
                        value={i.lot_size}
                        min={1}
                        onChange={(v) => updateInstrument(idx, { lot_size: v })}
                        className="w-20 sm:w-16 border border-border rounded px-2 py-2 sm:py-1 font-mono text-base sm:text-xs"
                      />
                    </td>
                    <td className="py-1 pr-2">
                      <select
                        value={i.broker_connection_id}
                        onChange={(e) =>
                          updateInstrument(idx, { broker_connection_id: e.target.value })
                        }
                        className="w-full border border-border rounded px-2 py-1 text-xs"
                      >
                        <option value="">— pick —</option>
                        {routableConnections.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.broker}/{b.label}
                          </option>
                        ))}
                        {/* Keep an already-saved but now-ineligible connection
                            visible so editing an existing deployment shows what
                            it points at instead of silently blanking the field. */}
                        {i.broker_connection_id &&
                          !routableConnections.some(
                            (b) => b.id === i.broker_connection_id,
                          ) && (
                            <option value={i.broker_connection_id} disabled>
                              {brokers?.find((b) => b.id === i.broker_connection_id)
                                ? `${brokers.find((b) => b.id === i.broker_connection_id)!.broker}/${brokers.find((b) => b.id === i.broker_connection_id)!.label} — not available`
                                : "unavailable connection"}
                            </option>
                          )}
                      </select>
                    </td>
                    <td className="py-1 text-right">
                      <button
                        type="button"
                        onClick={() => removeInstrument(idx)}
                        className="text-down-600 dark:text-down-400 text-xs hover:underline"
                      >
                        remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {error && (
          <div className="text-sm text-down-700 dark:text-down-400 bg-down-50 dark:bg-down-500/10 border border-down-200 dark:border-down-400/30 rounded px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <Button type="submit" disabled={saving}>
            {saving
              ? editing
                ? "Saving…"
                : "Creating…"
              : editing
                ? "Save changes"
                : "Create deployment"}
          </Button>
          <Link to={editing ? `/deployments/${editId}` : "/deployments"}>
            <Button type="button" variant="ghost">
              Cancel
            </Button>
          </Link>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-fg-subtle mb-1">{label}</label>
      {children}
    </div>
  );
}
